import { useNotifications } from '@/composables/useNotifications'
import type { NotificationOptions } from '@/types/notifications'
import type { ApiErrorResponse } from '@/types/configuration'

/**
 * ApiError class interface for type checking
 */
interface ApiError extends Error {
  response: ApiErrorResponse
  status: number
}

/**
 * Utility function to wrap API calls with automatic notifications
 */
export async function withApiNotifications<T>(
  apiCall: () => Promise<T>,
  options: {
    loadingMessage?: string
    successMessage?: string
    errorMessage?: string
    showSuccess?: boolean
    showError?: boolean
    notificationOptions?: Partial<NotificationOptions>
  } = {}
): Promise<T> {
  const {
    loadingMessage,
    successMessage = 'Operation completed successfully',
    errorMessage = 'Operation failed',
    showSuccess = false,
    showError = true,
    notificationOptions = {}
  } = options

  const { info, success, error, handleClose } = useNotifications()
  
  let loadingNotificationId: string | undefined
  let loadingStartTime: number | undefined
  const MIN_DISPLAY_TIME = 800 // Minimum time to show loading notification (ms)

  try {
    // Show loading notification if specified
    if (loadingMessage) {
      loadingStartTime = Date.now()
      loadingNotificationId = info(loadingMessage, {
        timeout: 0, // No auto-close timer - will be manually closed
        ...notificationOptions
      })
    }

    // Execute the API call
    const result = await apiCall()

    // Hide loading notification with minimum display time
    if (loadingNotificationId && loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed)
      
      // Use setTimeout to ensure minimum display time
      if (remainingTime > 0) {
        setTimeout(() => {
          handleClose(loadingNotificationId!)
        }, remainingTime)
      } else {
        // Close immediately if minimum time has passed
        handleClose(loadingNotificationId)
      }
    }

    // Show success notification
    if (showSuccess && successMessage) {
      success(successMessage, notificationOptions)
    }

    return result
  } catch (apiError) {
    // Hide loading notification with minimum display time
    if (loadingNotificationId && loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed)
      
      // Use setTimeout to ensure minimum display time
      if (remainingTime > 0) {
        setTimeout(() => {
          handleClose(loadingNotificationId!)
        }, remainingTime)
      } else {
        // Close immediately if minimum time has passed
        handleClose(loadingNotificationId)
      }
    }

    // Show error notification
    if (showError) {
      // Extract meaningful error message from ApiError or use fallback
      let message = errorMessage
      
      // Check if it's an ApiError with response data
      if (apiError && typeof apiError === 'object' && 'response' in apiError) {
        const apiErr = apiError as ApiError
        if (apiErr.response?.message) {
          message = apiErr.response.message
        } else if (apiErr.message && apiErr.message.trim() !== '') {
          message = apiErr.message
        }
      } else if (apiError instanceof Error && apiError.message && apiError.message.trim() !== '') {
        // Standard Error with message
        message = apiError.message
      }
      
      // Log error details for debugging
      console.error('API Error:', {
        error: apiError,
        extractedMessage: message,
        fallbackMessage: errorMessage
      })
      
      error(message, {
        actions: {
          primary: [
            {
              id: 'dismiss',
              label: 'Dismiss',
              onClick: () => {}
            }
          ]
        },
        ...notificationOptions
      })
    }

    // Re-throw the error so calling code can handle it
    throw apiError
  }
}

/**
 * Common notification messages for different API operations
 */
export const API_MESSAGES = {
  LOADING: {
    GET_TENANTS: 'Loading tenants...',
    GET_CONFIGURATION: 'Loading configuration...',
    APPLY_PATCH: 'Applying changes...',
    CLEAR_CACHE: 'Clearing cache...',
    VALIDATE_SCHEMA: 'Validating schema...',
    TEST_TEMPLATE: 'Testing template...',
    GET_AUDIT: 'Loading audit data...'
  },
  SUCCESS: {
    GET_TENANTS: 'Tenants loaded successfully',
    GET_CONFIGURATION: 'Configuration loaded successfully',
    APPLY_PATCH: 'Changes applied successfully',
    CLEAR_CACHE: 'Cache cleared successfully',
    VALIDATE_SCHEMA: 'Schema validation completed',
    TEST_TEMPLATE: 'Template test completed',
    GET_AUDIT: 'Audit data loaded successfully'
  },
  ERROR: {
    GET_TENANTS: 'Failed to load tenants',
    GET_CONFIGURATION: 'Failed to load configuration',
    APPLY_PATCH: 'Failed to apply changes',
    CLEAR_CACHE: 'Failed to clear cache',
    VALIDATE_SCHEMA: 'Schema validation failed',
    TEST_TEMPLATE: 'Template test failed',
    GET_AUDIT: 'Failed to load audit data'
  }
}
